import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { MetricCard } from '@/components/financials/MetricCard';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { BRAND, CHART_SERIES } from '@/lib/brand';
import { formatMonthLabel, normaliseMonth } from '@/lib/months';
import { useProjectionsCapital } from '@/features/projections/hooks';
import { useChartOfAccounts } from '@/features/financials/hooks';
import type { Tables } from '@/types/database';
import { CAPITAL_CATEGORIES } from './CapitalItemPage';

const CATEGORY_LABEL = new Map<string, string>(
  CAPITAL_CATEGORIES.map((c) => [c.value, c.label]),
);

/**
 * Capital Planning — a chart + list of large planned transactions. Each row
 * is an "item" (equipment order, investor injection, loan paydown, dividend,
 * tax payment). "Add item" and clicking a row both route to the dedicated
 * item detail page where the full item — including its Chart-of-Accounts
 * category — is edited. Items flow into the projected cashflow and P&L.
 */
export function CapitalPlanningTab() {
  const navigate = useNavigate();
  const { data, isLoading } = useProjectionsCapital();
  const { data: coa } = useChartOfAccounts();

  const coaById = useMemo(
    () => new Map((coa ?? []).map((a) => [a.id, a])),
    [coa],
  );

  const totals = useMemo(() => {
    const inflow = (data ?? [])
      .filter((r) => r.direction === 'credit')
      .reduce((a, r) => a + r.amount_cents, 0);
    const outflow = (data ?? [])
      .filter((r) => r.direction === 'debit')
      .reduce((a, r) => a + r.amount_cents, 0);
    return { inflow, outflow, net: inflow - outflow };
  }, [data]);

  /** Monthly inflow / outflow bars from each item's projected date. */
  const chartData = useMemo(() => {
    const byMonth = new Map<string, { inflow: number; outflow: number }>();
    for (const r of data ?? []) {
      const month = normaliseMonth(r.txn_date);
      const entry = byMonth.get(month) ?? { inflow: 0, outflow: 0 };
      if (r.direction === 'credit') entry.inflow += r.amount_cents / 100;
      else entry.outflow += r.amount_cents / 100;
      byMonth.set(month, entry);
    }
    return Array.from(byMonth.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, v]) => ({
        month: formatMonthLabel(month),
        inflow: v.inflow,
        outflow: v.outflow,
      }));
  }, [data]);

  const rows = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.txn_date.localeCompare(b.txn_date)),
    [data],
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard label="Planned inflow" value={formatCurrency(totals.inflow)} loading={isLoading} />
        <MetricCard label="Planned outflow" value={formatCurrency(totals.outflow)} loading={isLoading} />
        <MetricCard label="Net capital movement" value={formatCurrency(totals.net)} loading={isLoading} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Capital flows by month</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-72" />
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground">No capital items yet.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
                  <XAxis dataKey="month" stroke={BRAND.oldGrowth} fontSize={11} />
                  <YAxis
                    stroke={BRAND.oldGrowth}
                    fontSize={11}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(v: number) => `$${v.toLocaleString()}`}
                    contentStyle={{ background: '#fff', border: `1px solid ${BRAND.linenDark}`, borderRadius: 8 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="inflow" name="Inflow" fill={CHART_SERIES[0]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outflow" name="Outflow" fill={CHART_SERIES[4]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Capital items</CardTitle>
          </div>
          <Button onClick={() => navigate('/financials/projections/capital/new')}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            Add item
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0 pb-6">
          {isLoading ? (
            <Skeleton className="mx-6 h-32" />
          ) : rows.length === 0 ? (
            <p className="px-6 text-sm text-muted-foreground">No capital items yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <Th>Date</Th>
                  <Th>Description</Th>
                  <Th>Category</Th>
                  <Th>COA account</Th>
                  <Th className="text-right">Inflow</Th>
                  <Th className="text-right">Outflow</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <CapitalRow
                    key={row.id}
                    row={row}
                    coaName={
                      row.qb_account_id ? coaById.get(row.qb_account_id)?.name ?? null : null
                    }
                    onClick={() => navigate(`/financials/projections/capital/${row.id}`)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground ${className ?? ''}`}
    >
      {children}
    </th>
  );
}

function CapitalRow({
  row,
  coaName,
  onClick,
}: {
  row: Tables<'projections_capital'>;
  coaName: string | null;
  onClick: () => void;
}) {
  return (
    <tr
      className="cursor-pointer border-b border-border last:border-0 hover:bg-secondary/30"
      onClick={onClick}
    >
      <td className="px-4 py-2.5 tabular-nums">{formatDate(row.txn_date)}</td>
      <td className="px-4 py-2.5 min-w-[12rem] font-medium text-foreground">{row.description}</td>
      <td className="px-4 py-2.5 text-muted-foreground">
        {CATEGORY_LABEL.get(row.category) ?? row.category}
      </td>
      <td className="px-4 py-2.5 text-muted-foreground">{coaName ?? '—'}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-bright-moss-dark">
        {row.direction === 'credit' ? formatCurrency(row.amount_cents) : '—'}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums text-fresh-salmon-dark">
        {row.direction === 'debit' ? formatCurrency(row.amount_cents) : '—'}
      </td>
    </tr>
  );
}
